import { z } from "zod";

/**
 * The auth contract, shared by both sides: the server builds these payloads, the
 * client parses them with the same schemas. See docs/06-api.md#auth.
 *
 * `AppRole` is duplicated here rather than imported from `@prisma/client` because
 * src/shared has to run in a browser. The pair is kept honest by a test that
 * compares this enum against Prisma's.
 */
export const appRoleSchema = z.enum(["VIEWER", "PRODUCT_OWNER", "ADMIN"]);
export type AppRoleName = z.infer<typeof appRoleSchema>;

export const loginRequestSchema = z.object({
  // Lower-cased here, once, so the allow list, the user row and the audit trail
  // all key on the same string no matter how it was typed.
  email: z.string().trim().toLowerCase().email("Enter a valid email address.").max(254),
  password: z.string().min(1, "Enter your password.").max(512),
});
export type LoginRequest = z.infer<typeof loginRequestSchema>;

export const sessionUserSchema = z.object({
  id: z.string().min(1),
  email: z.string().email(),
  fullName: z.string(),
  nickName: z.string().nullable(),
  role: appRoleSchema,
});
export type SessionUser = z.infer<typeof sessionUserSchema>;

export const authPermissionsSchema = z.object({
  canCreateProduct: z.boolean(),
  isAdmin: z.boolean(),
});
export type AuthPermissions = z.infer<typeof authPermissionsSchema>;

export const loginResponseSchema = z.object({ user: sessionUserSchema });
export type LoginResponse = z.infer<typeof loginResponseSchema>;

export const meResponseSchema = z.object({
  user: sessionUserSchema,
  permissions: authPermissionsSchema,
});
export type MeResponse = z.infer<typeof meResponseSchema>;

/** VIEWER < PRODUCT_OWNER < ADMIN. Ordering exists only for `roleAtLeast`. */
const ROLE_RANK: Record<AppRoleName, number> = { VIEWER: 0, PRODUCT_OWNER: 1, ADMIN: 2 };

export function roleAtLeast(role: AppRoleName, minimum: AppRoleName): boolean {
  return ROLE_RANK[role] >= ROLE_RANK[minimum];
}

/**
 * Derived in one place so the button the client hides and the guard the server
 * enforces cannot drift. The guard is the control; the hidden button is manners.
 */
export function permissionsFor(role: AppRoleName): AuthPermissions {
  return {
    canCreateProduct: roleAtLeast(role, "PRODUCT_OWNER"),
    isAdmin: role === "ADMIN",
  };
}
